import * as THREE from "three";
import type { FrameContext, WeatherKind } from "../core/types";
import { hashString, seededRandom } from "../core/random";
import type { ChunkBounds } from "./geometry";
import { JiangnanWorldModel } from "./model";

export type WorldVegetationKind = "grass" | "fern" | "shrub" | "cypress";
export type EcologyMicroEventKind = "dragonfly" | "fish-glint" | "leaf-drift";

export interface EcologyAssetPlacement {
  kind: WorldVegetationKind;
  x: number;
  z: number;
  scale: readonly [number, number, number];
  rotation: number;
  variant: number;
}

export interface EcologyPlacement {
  x: number;
  y: number;
  z: number;
  scale: number;
  rotation: number;
  phase: number;
}

export interface EcologyMicroEvent {
  kind: EcologyMicroEventKind;
  x: number;
  y: number;
  z: number;
  phase: number;
}

export interface WatersideEcologyPlan {
  assets: EcologyAssetPlacement[];
  groundClusters: EcologyPlacement[];
  bankSeams: EcologyPlacement[];
  reeds: EcologyPlacement[];
  cattails: EcologyPlacement[];
  smartweed: EcologyPlacement[];
  floatingLeaves: EcologyPlacement[];
  events: EcologyMicroEvent[];
}

interface DynamicInstances {
  mesh: THREE.InstancedMesh;
  placements: readonly EcologyPlacement[];
  sway: number;
  drift: boolean;
}

interface RuntimeEvent extends EcologyMicroEvent {
  object: THREE.Object3D;
}

export interface WatersideEcologyChunk {
  group: THREE.Group;
  assets: readonly EcologyAssetPlacement[];
  dynamics: DynamicInstances[];
  events: RuntimeEvent[];
  alive: boolean;
}

const matrix = new THREE.Matrix4();
const position = new THREE.Vector3();
const quaternion = new THREE.Quaternion();
const scale = new THREE.Vector3();
const euler = new THREE.Euler();

function ecologyPlacement(
  random: () => number,
  x: number,
  y: number,
  z: number,
  minScale: number,
  scaleRange: number,
): EcologyPlacement {
  return {
    x,
    y,
    z,
    scale: minScale + random() * scaleRange,
    rotation: random() * Math.PI * 2,
    phase: random() * Math.PI * 2,
  };
}

function assetPlacement(
  kind: WorldVegetationKind,
  random: () => number,
  x: number,
  z: number,
  scaleValue: number,
  heightVariation = 0,
): EcologyAssetPlacement {
  const width = scaleValue * (0.9 + random() * 0.2);
  return {
    kind,
    x,
    z,
    scale: [width, scaleValue * (1 + (random() - 0.5) * heightVariation), width],
    rotation: random() * Math.PI * 2,
    variant: Math.floor(random() * 16),
  };
}

function isNearWater(model: JiangnanWorldModel, x: number, z: number, radius: number): boolean {
  return model.getWaterDepth(x + radius, z) > 0
    || model.getWaterDepth(x - radius, z) > 0
    || model.getWaterDepth(x, z + radius) > 0
    || model.getWaterDepth(x, z - radius) > 0;
}

export function createWatersideEcologyPlan(
  model: JiangnanWorldModel,
  bounds: ChunkBounds,
  chunkX: number,
  chunkZ: number,
  detail: "near" | "far",
): WatersideEcologyPlan {
  const random = seededRandom(`${model.version}:${model.seed}:waterside-ecology:${chunkX}:${chunkZ}`);
  const plan: WatersideEcologyPlan = {
    assets: [],
    groundClusters: [],
    bankSeams: [],
    reeds: [],
    cattails: [],
    smartweed: [],
    floatingLeaves: [],
    events: [],
  };
  const sampleCount = detail === "near" ? 240 : 128;
  const waterCandidates: EcologyPlacement[] = [];
  const watersideCandidates: EcologyPlacement[] = [];
  const farTreeCandidates: Array<{ x: number; z: number }> = [];

  for (let index = 0; index < sampleCount; index += 1) {
    const x = bounds.minX + random() * bounds.size;
    const z = bounds.minZ + random() * bounds.size;
    const point = model.getPoint(x, z);
    const floraRoll = hashString(`${model.seed}:flora:${Math.floor(x / 7)}:${Math.floor(z / 7)}:${index}`) % 100;

    if (point.feature === "revetment") {
      const placement = ecologyPlacement(random, x, point.height + 0.015, z, 0.66, 0.56);
      watersideCandidates.push(placement);
      if (floraRoll < (detail === "near" ? 72 : 36)) plan.bankSeams.push(placement);
      continue;
    }

    if (point.surface === "water") {
      const placement = ecologyPlacement(random, x, (point.waterHeight ?? point.height) + 0.018, z, 0.72, 0.48);
      waterCandidates.push(placement);
      if (detail === "near" && point.waterDepth < 0.62) {
        if (floraRoll < 30) plan.reeds.push(placement);
        else if (floraRoll < 43) plan.cattails.push(placement);
      } else if (detail === "near" && point.waterDepth < 1.18 && floraRoll < 9) {
        plan.floatingLeaves.push(placement);
      } else if (detail === "far" && point.waterDepth < 0.62) {
        if (floraRoll < 14) plan.reeds.push(placement);
        else if (floraRoll < 19) plan.cattails.push(placement);
      }
      continue;
    }

    if (point.walkable || point.feature === "home-courtyard") continue;
    const nearWater = isNearWater(model, x, z, 2.4);
    if ((point.feature === "garden" || point.surface === "earth") && nearWater && floraRoll < (detail === "near" ? 24 : 18)) {
      plan.smartweed.push(ecologyPlacement(random, x, point.height + 0.01, z, 0.7, 0.5));
    }

    if (point.surface === "grass" || point.surface === "earth" || point.feature === "garden") {
      if (floraRoll < (detail === "near" ? 52 : 64)) {
        plan.groundClusters.push(ecologyPlacement(random, x, point.height + 0.008, z, 0.62, 0.64));
      }
      if (detail === "far" && (point.surface === "grass" || point.feature === "garden")) {
        farTreeCandidates.push({ x, z });
      }
    }

    if (point.surface !== "grass") continue;
    if (detail === "near" && floraRoll < 34) {
      plan.assets.push(assetPlacement("grass", random, x, z, 0.7 + random() * 0.48));
    } else if (detail === "near" && floraRoll < 52) {
      plan.assets.push(assetPlacement("fern", random, x, z, 0.72 + random() * 0.4));
    } else if (floraRoll > (detail === "near" ? 90 : 96) && plan.assets.filter((entry) => entry.kind === "shrub").length < (detail === "near" ? 10 : 1)) {
      plan.assets.push(assetPlacement("shrub", random, x, z, 0.76 + random() * 0.38, 0.18));
    } else if (
      detail === "near"
      && floraRoll >= 73
      && floraRoll < 77
      && point.feature === "garden"
      && plan.assets.filter((entry) => entry.kind === "cypress").length < 3
    ) {
      plan.assets.push(assetPlacement("cypress", random, x, z, 0.18 + random() * 0.09, 0.32));
    }
  }

  if (detail === "far" && farTreeCandidates.length > 0) {
    const treeRoll = hashString(`${model.version}:${model.seed}:far-tree:${chunkX}:${chunkZ}`);
    if (treeRoll % 3 !== 0) {
      const candidate = farTreeCandidates[treeRoll % farTreeCandidates.length]!;
      plan.assets.push(assetPlacement("cypress", random, candidate.x, candidate.z, 0.16 + random() * 0.08, 0.24));
    }
  }

  if (detail === "near") {
    const fixedAssets: Array<[WorldVegetationKind, number, number, number]> = [
      ["shrub", 9.8, 10.8, 0.92],
      ["cypress", -6.5, -1.5, 0.25],
      ["cypress", 6.8, -7.4, 0.2],
      ["fern", -4.9, 5.1, 0.86],
      ["fern", 4.95, 3.4, 0.74],
    ];
    for (const [kind, x, z, scaleValue] of fixedAssets) {
      if (x >= bounds.minX && x < bounds.minX + bounds.size && z >= bounds.minZ && z < bounds.minZ + bounds.size) {
        plan.assets.push(assetPlacement(kind, random, x, z, scaleValue, kind === "cypress" ? 0.2 : 0.08));
      }
    }

    const eventRoll = hashString(`${model.version}:${model.seed}:ecology-event:${chunkX}:${chunkZ}`) % 100;
    const waterside = watersideCandidates[0];
    const water = waterCandidates[0];
    if (eventRoll < 9 && waterside) {
      plan.events.push({ kind: "dragonfly", x: waterside.x, y: waterside.y + 0.72, z: waterside.z, phase: random() * 31 });
    } else if (eventRoll < 16 && water) {
      plan.events.push({ kind: "fish-glint", x: water.x, y: water.y - 0.025, z: water.z, phase: random() * 31 });
    } else if (eventRoll < 23 && water) {
      plan.events.push({ kind: "leaf-drift", x: water.x, y: water.y + 0.012, z: water.z, phase: random() * 31 });
    }
  }

  return plan;
}

function windFor(weather: WeatherKind): number {
  switch (weather) {
    case "mist": return 0.28;
    case "clear": return 0.46;
    case "after-rain": return 0.58;
    case "cloudy": return 0.66;
    case "overcast": return 0.72;
    case "drizzle": return 0.92;
  }
}

export class WatersideEcology {
  private readonly model: JiangnanWorldModel;
  private readonly chunks = new Set<WatersideEcologyChunk>();
  private readonly geometries: THREE.BufferGeometry[];
  private readonly materials: THREE.Material[];
  private readonly seamGeometry = new THREE.PlaneGeometry(0.055, 0.38).translate(0, 0.19, 0);
  private readonly groundClusterGeometry = new THREE.ConeGeometry(0.24, 0.42, 3).translate(0, 0.21, 0);
  private readonly reedGeometry = new THREE.PlaneGeometry(0.065, 0.94).translate(0, 0.47, 0);
  private readonly stemGeometry = new THREE.CylinderGeometry(0.011, 0.018, 1.05, 5).translate(0, 0.525, 0);
  private readonly cattailGeometry = new THREE.CylinderGeometry(0.027, 0.035, 0.18, 6).translate(0, 1.05, 0);
  private readonly smartweedGeometry = new THREE.ConeGeometry(0.035, 0.24, 5).translate(0, 0.42, 0);
  private readonly leafGeometry = new THREE.CircleGeometry(0.13, 8).rotateX(-Math.PI / 2);
  private readonly wingGeometry = new THREE.PlaneGeometry(0.18, 0.035);
  private readonly insectBodyGeometry = new THREE.CylinderGeometry(0.012, 0.016, 0.15, 5).rotateZ(Math.PI / 2);
  private readonly glintGeometry = new THREE.PlaneGeometry(0.26, 0.025).rotateX(-Math.PI / 2);
  private readonly seamMaterial = new THREE.MeshStandardMaterial({ color: 0x445b39, roughness: 0.96, side: THREE.DoubleSide });
  private readonly groundClusterMaterial = new THREE.MeshStandardMaterial({ color: 0x53663d, roughness: 0.98, side: THREE.DoubleSide });
  private readonly reedMaterial = new THREE.MeshStandardMaterial({ color: 0x5e713d, roughness: 0.94, side: THREE.DoubleSide });
  private readonly cattailMaterial = new THREE.MeshStandardMaterial({ color: 0x69543a, roughness: 0.98 });
  private readonly smartweedMaterial = new THREE.MeshStandardMaterial({ color: 0x8b5865, roughness: 0.9 });
  private readonly leafMaterial = new THREE.MeshStandardMaterial({ color: 0x496b43, roughness: 0.88, side: THREE.DoubleSide });
  private readonly wingMaterial = new THREE.MeshBasicMaterial({ color: 0x8aa2a0, transparent: true, opacity: 0.58, side: THREE.DoubleSide });
  private readonly insectBodyMaterial = new THREE.MeshStandardMaterial({ color: 0x263e36, roughness: 0.65 });
  private readonly glintMaterial = new THREE.MeshBasicMaterial({ color: 0xd4ded1, transparent: true, opacity: 0.52, side: THREE.DoubleSide });

  constructor(model: JiangnanWorldModel) {
    this.model = model;
    this.geometries = [
      this.seamGeometry,
      this.groundClusterGeometry,
      this.reedGeometry,
      this.stemGeometry,
      this.cattailGeometry,
      this.smartweedGeometry,
      this.leafGeometry,
      this.wingGeometry,
      this.insectBodyGeometry,
      this.glintGeometry,
    ];
    this.materials = [
      this.seamMaterial,
      this.groundClusterMaterial,
      this.reedMaterial,
      this.cattailMaterial,
      this.smartweedMaterial,
      this.leafMaterial,
      this.wingMaterial,
      this.insectBodyMaterial,
      this.glintMaterial,
    ];
  }

  createChunk(bounds: ChunkBounds, chunkX: number, chunkZ: number, detail: "near" | "far"): WatersideEcologyChunk {
    const plan = createWatersideEcologyPlan(this.model, bounds, chunkX, chunkZ, detail);
    const group = new THREE.Group();
    group.name = `waterside-ecology-${chunkX}-${chunkZ}-${detail}`;
    const chunk: WatersideEcologyChunk = { group, assets: plan.assets, dynamics: [], events: [], alive: true };

    const sway = detail === "near" ? 1 : 0;
    this.addInstances(chunk, "ground-cover-clusters", this.groundClusterGeometry, this.groundClusterMaterial, plan.groundClusters, 0, false);
    this.addInstances(chunk, "hard-bank-seams", this.seamGeometry, this.seamMaterial, plan.bankSeams, 0.75 * sway, false);
    this.addInstances(chunk, "reeds", this.reedGeometry, this.reedMaterial, plan.reeds, sway, false);
    this.addInstances(chunk, "cattail-stems", this.stemGeometry, this.reedMaterial, plan.cattails, 0.85 * sway, false);
    this.addInstances(chunk, "cattail-heads", this.cattailGeometry, this.cattailMaterial, plan.cattails, 0.85 * sway, false);
    this.addInstances(chunk, "water-smartweed", this.smartweedGeometry, this.smartweedMaterial, plan.smartweed, 0.68 * sway, false);
    this.addInstances(chunk, "floating-leaves", this.leafGeometry, this.leafMaterial, plan.floatingLeaves, 0, true);
    for (const event of plan.events) chunk.events.push({ ...event, object: this.createEventObject(event) });
    for (const event of chunk.events) group.add(event.object);
    this.chunks.add(chunk);
    return chunk;
  }

  update(context: FrameContext): void {
    if (context.paused) return;
    const wind = windFor(context.settings.weather);
    for (const chunk of this.chunks) {
      if (!chunk.alive) continue;
      for (const dynamic of chunk.dynamics) this.updateInstances(dynamic, context.elapsed, wind);
      for (const event of chunk.events) this.updateEvent(event, context, wind);
    }
  }

  removeChunk(chunk: WatersideEcologyChunk): void {
    if (!chunk.alive) return;
    chunk.alive = false;
    chunk.group.removeFromParent();
    chunk.dynamics.length = 0;
    chunk.events.length = 0;
    this.chunks.delete(chunk);
  }

  dispose(): void {
    for (const chunk of [...this.chunks]) this.removeChunk(chunk);
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
  }

  private addInstances(
    chunk: WatersideEcologyChunk,
    name: string,
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    placements: readonly EcologyPlacement[],
    sway: number,
    drift: boolean,
  ): void {
    if (placements.length === 0) return;
    const mesh = new THREE.InstancedMesh(geometry, material, placements.length);
    mesh.name = name;
    mesh.castShadow = name !== "floating-leaves" && name !== "ground-cover-clusters";
    mesh.receiveShadow = true;
    if (sway > 0 || drift) mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    const dynamic = { mesh, placements, sway, drift };
    this.updateInstances(dynamic, 0, 0);
    if (sway > 0 || drift) chunk.dynamics.push(dynamic);
    chunk.group.add(mesh);
  }

  private updateInstances(dynamic: DynamicInstances, elapsed: number, wind: number): void {
    for (let index = 0; index < dynamic.placements.length; index += 1) {
      const placement = dynamic.placements[index]!;
      const sway = Math.sin(elapsed * 0.72 + placement.phase) * 0.045 * wind * dynamic.sway;
      const driftX = dynamic.drift ? Math.sin(elapsed * (0.045 + wind * 0.025) + placement.phase) * 0.34 : 0;
      const driftZ = dynamic.drift ? Math.cos(elapsed * (0.035 + wind * 0.02) + placement.phase) * 0.22 : 0;
      const bob = dynamic.drift ? Math.sin(elapsed * 0.55 + placement.phase) * 0.008 : 0;
      position.set(placement.x + driftX, placement.y + bob, placement.z + driftZ);
      euler.set(sway, placement.rotation + (dynamic.drift ? elapsed * 0.018 * wind : 0), sway * 0.65);
      quaternion.setFromEuler(euler);
      scale.setScalar(placement.scale);
      matrix.compose(position, quaternion, scale);
      dynamic.mesh.setMatrixAt(index, matrix);
    }
    dynamic.mesh.instanceMatrix.needsUpdate = true;
  }

  private createEventObject(event: EcologyMicroEvent): THREE.Object3D {
    const group = new THREE.Group();
    group.name = `ecology-event-${event.kind}`;
    group.position.set(event.x, event.y, event.z);
    if (event.kind === "dragonfly") {
      const leftWing = new THREE.Mesh(this.wingGeometry, this.wingMaterial);
      const rightWing = new THREE.Mesh(this.wingGeometry, this.wingMaterial);
      leftWing.rotation.z = 0.22;
      rightWing.rotation.z = Math.PI - 0.22;
      group.add(leftWing, rightWing, new THREE.Mesh(this.insectBodyGeometry, this.insectBodyMaterial));
    } else if (event.kind === "fish-glint") {
      group.add(new THREE.Mesh(this.glintGeometry, this.glintMaterial));
    } else {
      group.add(new THREE.Mesh(this.leafGeometry, this.leafMaterial));
    }
    group.visible = false;
    return group;
  }

  private updateEvent(event: RuntimeEvent, context: FrameContext, wind: number): void {
    const daytime = context.settings.timePreset !== "night";
    const calmEnough = context.settings.weather !== "drizzle" && context.settings.weather !== "mist";
    if (event.kind === "dragonfly") {
      const cycle = (context.elapsed + event.phase) % 29;
      event.object.visible = context.settings.animals && daytime && calmEnough && cycle < 1.8;
      event.object.position.set(
        event.x + Math.sin(context.elapsed * 2.2 + event.phase) * 0.7,
        event.y + Math.sin(context.elapsed * 3.1 + event.phase) * 0.12,
        event.z + Math.cos(context.elapsed * 1.85 + event.phase) * 0.48,
      );
      event.object.rotation.y = context.elapsed * 1.7 + event.phase;
    } else if (event.kind === "fish-glint") {
      const cycle = (context.elapsed + event.phase) % 23;
      event.object.visible = context.settings.animals && daytime && context.settings.weather !== "drizzle" && cycle < 0.72;
      event.object.position.set(event.x + cycle * 0.65, event.y, event.z + Math.sin(event.phase) * 0.2);
      event.object.scale.setScalar(0.55 + Math.sin(Math.min(cycle / 0.72, 1) * Math.PI) * 0.75);
    } else {
      event.object.visible = true;
      const drift = context.elapsed * (0.018 + wind * 0.025);
      event.object.position.set(event.x + Math.sin(drift + event.phase) * 0.72, event.y, event.z + Math.cos(drift * 0.8 + event.phase) * 0.46);
      event.object.rotation.y = drift * 0.7;
    }
  }
}
